import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import {
  GET_MY_ADS_LIST,
  getCurrentUserAdStatus,
  MyAdsListResponse,
  MyAdsListVariables,
} from '@/features/Profile/sections/MyAds/api';
import { AdStatus } from '@/types';
import { useOnlineStatus } from '@/features/Profile/core/hooks';

export function useMyAdsList(selectedAdStatus: AdStatus) {
  const [isLoadingMore, setLoadingMore] = useState(false);
  const isOnline = useOnlineStatus();
  const queryStatus = getCurrentUserAdStatus(selectedAdStatus);
  const { data, loading, fetchMore } = useQuery<
    MyAdsListResponse,
    MyAdsListVariables
  >(GET_MY_ADS_LIST, {
    variables: {
      status: queryStatus,
      first: AdsChunkSize.Middle,
    },
    fetchPolicy: 'cache-and-network',
  });

  const ads = data?.currentUserAds?.nodes ?? [];
  const { endCursor, hasNextPage } = data?.currentUserAds?.pageInfo ?? {};
  const canLoadMore = !!hasNextPage && isOnline;
  const isInitialLoading = loading && ads.length === 0;

  function handleLoadMore() {
    if (loading || isLoadingMore || !canLoadMore) return;

    setLoadingMore(true);
    fetchMore({
      variables: {
        after: endCursor,
      },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        if (!prevResult.currentUserAds || !fetchMoreResult?.currentUserAds) {
          return prevResult;
        }

        return {
          ...prevResult,
          currentUserAds: {
            ...prevResult.currentUserAds,
            nodes: [
              ...(prevResult.currentUserAds.nodes ?? []),
              ...(fetchMoreResult.currentUserAds.nodes ?? []),
            ],
            pageInfo: fetchMoreResult.currentUserAds.pageInfo,
          },
        };
      },
    }).finally(() => setLoadingMore(false));
  }

  return {
    ads,
    canLoadMore,
    handleLoadMore,
    isLoadingMore,
    loading: isInitialLoading,
  };
}
